import {
  Element,
  LogicCondition,
  LogicIteration,
  PageElement,
} from "@easy-email-pro-core/typings";
import { BlockManager } from '../BlockManager';

export enum PluginType {
  RESPONSIVE = "RESPONSIVE",
  TEMPLATE_ENGINE = "TEMPLATE_ENGINE",
}

export abstract class ResponsivePlugin {
  static type = PluginType.RESPONSIVE;
  public enabledResponsive = true;

  public abstract generateResponsive(params: {
    node: Element;
    mode: "testing" | "production";
    context: { content: PageElement; };
    children?: React.ReactNode;
    displayMode: "only-desktop" | "only-mobile" | "desktop-mobile" | undefined;
    keepEmptyAttributes: boolean;
    mergetagsData?: Record<string, any>;
    idx?: string | null | undefined;
  }): React.ReactNode;
}

export abstract class TemplateEnginePlugin {
  public static type = PluginType.TEMPLATE_ENGINE;

  public abstract generateIterationTemplate(
    option: LogicIteration,
    content: React.ReactNode
  ): React.ReactNode;

  public abstract generateConditionTemplate(
    option: LogicCondition | string,
    content: React.ReactNode,
    fallback?: React.ReactNode
  ): React.ReactNode;

  abstract isVariable(variable: string): boolean;

  abstract generateVariable(variable: string, defaultValue?: string): string;

  abstract renderWithData(html: string, data: Record<string, any>): string;
}

export abstract class ElementPlugin {
  public abstract generateElement(): void;
}

type Plugin = ResponsivePlugin | TemplateEnginePlugin | ElementPlugin;

export class PluginManager {
  public static enabledResponsive = false;

  public static generateResponsive: ResponsivePlugin["generateResponsive"] = (
    params
  ) => {
    const block = BlockManager.getBlockByType(params.node.type);
    const { mode, children, context, idx, node, keepEmptyAttributes, mergetagsData } = params;
    return block.render({
      ...params,
      mode,
      children,
      context,
      idx,
      node: node as never,
      keepEmptyAttributes,
      mergetagsData,
    });
  };

  public static generateIterationTemplate: TemplateEnginePlugin["generateIterationTemplate"] =
    (option, content) => {
      return content;
    };
  public static isVariable: TemplateEnginePlugin["isVariable"] = (option) => {
    return false;
  };

  public static generateConditionTemplate: TemplateEnginePlugin["generateConditionTemplate"] =
    (option, content): React.ReactNode => {
      return content;
    };

  public static generateVariable: TemplateEnginePlugin["generateVariable"] = (
    variable
  ): string => {
    return variable;
  };
  public static renderWithData: TemplateEnginePlugin["renderWithData"] = (
    html: string,
    data: Record<string, any>
  ): string => {
    return html;
  };

  public static registerPlugins(plugins: Array<new () => Plugin>): void {
    plugins.forEach((Plug) => {
      PluginManager.registerPlugin(Plug);
    });
  }

  public static registerPlugin(Plug: new () => Plugin): void {
    const plugin = new Plug();
    if (plugin instanceof ResponsivePlugin) {
      // responsive
      PluginManager.enabledResponsive = plugin.enabledResponsive;
      PluginManager.generateResponsive = plugin.generateResponsive;
    } else if (plugin instanceof TemplateEnginePlugin) {
      //  template engine
      PluginManager.generateIterationTemplate =
        plugin.generateIterationTemplate;

      PluginManager.generateConditionTemplate =
        plugin.generateConditionTemplate;

      PluginManager.generateVariable = plugin.generateVariable;
      PluginManager.isVariable = plugin.isVariable;

      PluginManager.renderWithData = plugin.renderWithData;
    } else if (plugin instanceof ElementPlugin) {
      // element
      plugin.generateElement();
    }
  }
}
